/**
 * One SELF-PACED phase's cycle, projected out of the draft: an ordered run of training days, each
 * carrying the break the trainer prescribed after it. No weekdays, no dates — order and spacing are
 * the identity.
 *
 * The canvas that renders it is `Builder planu sekwencja.dc.html` (07.09), which draws the run as
 * numbered columns; `pages/plan-builder/model/tapeViewModel.ts` is the view half.
 *
 * ── What this module does NOT answer, and why the absence is the point ───────────────────────
 * It used to answer how long a cycle was: `Σ (1 + restDaysAfter)` over the phase's days, plus
 * `Σ restDaysAfter` as its own figure. Both are gone, with the engine that consumed them.
 * `self-paced-plan-requirements.md` R2 is explicit that a day's rest days "MUST NOT be summed into a
 * cycle length, and MUST NOT be used to derive the date of any occurrence", and R1's definition
 * closes the door from the other side: "In a self-paced plan a cycle has no length in days."
 *
 * **R2 as amended 2026-09-13 permits the sum to be DRAWN**, as a label in the trainer's builder,
 * where it dates nothing and reaches no wire field. That is `pages/plan-builder/model/cycleDays.ts`,
 * and it is deliberately NOT a field here: a `cycleLengthDays` on this projection would hand the
 * figure to every consumer of it, which is the door R2 holds shut. The scheduling arithmetic stays
 * unsound either way — a self-paced cycle spans however long the client takes. `restDaysAfter`
 * survives on the DAY, where R9 gives it its real meaning: the rest window the client's home surface
 * counts from the moment they FINISH that day's occurrence, which expires on its own and blocks
 * nothing (R10).
 *
 * ── Why it is a separate module from `weekProjection` ────────────────────────────────────────
 * The two share exactly one thing — `dayCard`, which is what a session card renders from — and that
 * is imported rather than duplicated. Everything else is different in kind: a week has seven fixed
 * columns and answers "which weekday is empty", a self-paced cycle has as many slots as it has days
 * and answers "what order do they come in". Branching one projection on the mode would make every
 * field of it conditionally meaningful, which is the shape that makes a caller read both halves to
 * trust either.
 *
 * ── A rest of ZERO is a real value, and it gets a column ─────────────────────────────────────
 * `restDaysAfter` is the ONLY surface on this screen that writes a recovery note, so a column that
 * vanished at zero would be a note the trainer can shorten and never get back. Every day gets one;
 * the zero one prints "bez przerwy" in words rather than a figure.
 *
 * `restDaysAfter` rides on the DAY rather than being a tape element of its own because the wire has
 * no rest row, and a tape that modelled one would need an identity for something the backend does
 * not store.
 *
 * ── The counts follow the WIRE, not the canvas ───────────────────────────────────────────────
 * A day with no exercise is dropped by `draftToRequest`'s `phaseToInput`, so it is not part of the
 * cycle the client will ever train. Counting it made the band claim a cycle the save then contradicted
 * — press "+ Trening" and "5 treningów" became "6 treningów", saved, and reloaded as five.
 * `countsToward` is what keeps the band and the wire agreeing; the day is still PROJECTED, because
 * it exists and the trainer has to be able to open and fill it.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { phaseStartCycles } from './cycleWindow';
import type { PlanDraft } from './planDraft.types';
import {
  dayCard,
  resolvePhaseIndex,
  toPhaseSummary,
  type DayCard,
  type ExerciseRow,
  type PhaseSummary,
} from './weekProjection';

/** One training of the cycle: its card, its position, and the recovery note after it. */
export interface TapeDay {
  /**
   * 1-based position in the cycle — what a move menu addresses this training by ("T3"), and what
   * names it when the trainer typed no name.
   *
   * NOT the day of the cycle it falls on: after a one-day break, training 2 falls on day 3. The
   * numbering that runs through the breaks is `cycleDays.ts`'s, one layer up.
   */
  readonly position: number;
  readonly card: DayCard;
  /**
   * The days of recovery the trainer prescribed AFTER this training, `0` when they prescribed none.
   * Always a number: the wire's `null` means "none given", which is the same coaching statement as
   * zero and is normalised here so no surface has to decide what a missing note means.
   *
   * **Advisory, never scheduling** (R2). It is not added to anything on this projection and nothing
   * downstream may add it to anything either — it becomes a live rest window measured from the
   * client's own finish timestamp (R9), which is a fact this builder never holds.
   */
  readonly restDaysAfter: number;
  /**
   * Whether this day survives serialisation, and therefore whether the cycle's numbers count it.
   *
   * `false` for a day carrying no exercise — `draftToRequest` drops it, so the client never trains
   * it and a count that included it would describe a cycle the next save contradicts. The day is
   * still drawn: it exists, the trainer just made it, and the column is where they fill it.
   */
  readonly countsToward: boolean;
}

export interface TapeProjection {
  readonly phase: PhaseSummary;
  /** The phase's days, in `Order`. Empty on a phase that programmes nothing. */
  readonly days: readonly TapeDay[];
  /** How many trainings the cycle holds — the canon's "5 treningów". Counted days only. */
  readonly trainingCount: number;
  /**
   * The mean authored duration over the days that carry one, rounded to a whole minute; `null` when
   * no day in the phase authored one. The canon's "~66 min / sesja śr." — per SESSION here, where
   * the week's counterpart is per DAY, because a self-paced cycle's days are not calendar days.
   */
  readonly averageDurationMinutes: number | null;
}

/** The recovery note after a day, normalised. `null` on the wire and `0` say the same thing. */
function restAfter(restDaysAfter: number | null): number {
  return restDaysAfter == null || restDaysAfter < 0 ? 0 : restDaysAfter;
}

/**
 * Project one phase of a self-paced plan into the cycle the sequence canvas draws.
 *
 * @param draft The loaded document.
 * @param phaseIndex Zero-based; out-of-range is clamped, exactly as `weekProjection` clamps it, so
 *   a stale `?phase=` in a bookmark answers with the nearest phase that exists.
 * @returns `null` when the plan has no phases. A plan in DATE mode is NOT refused here — this is a
 *   projection of days in order and a weekly plan has those too — because refusing would put the
 *   mode check in two places. The screen decides which canvas to draw; `PlanDraft.mode` is where
 *   that is read.
 */
export function tapeProjection(draft: PlanDraft, phaseIndex: number): TapeProjection | null {
  const index = resolvePhaseIndex(draft, phaseIndex);
  if (index === null) return null;
  const phase = draft.phases[index];
  if (phase === undefined) return null;

  const days = phase.days.map((day, offset) => ({
    position: offset + 1,
    card: dayCard(day),
    restDaysAfter: restAfter(day.restDaysAfter),
    // The same predicate `phaseToInput` filters on, asked here so the band and the wire agree.
    countsToward: day.exercises.length > 0,
  }));

  // Everything the CYCLE is measured over. An exercise-less day is drawn and out of the counts —
  // see the header, and `cycleDays.ts`, which excludes it from the cycle's length for the same
  // reason `draftToRequest` drops it.
  const counted = days.filter((day) => day.countsToward);
  const authored = counted
    .map((day) => day.card.durationMinutes)
    .filter((minutes): minutes is number => minutes != null);

  return {
    phase: toPhaseSummary(phase, index, phaseStartCycles(draft.phases)[index] ?? 1),
    days,
    trainingCount: counted.length,
    averageDurationMinutes:
      authored.length === 0
        ? null
        : Math.round(authored.reduce((sum, minutes) => sum + minutes, 0) / authored.length),
  };
}

/**
 * The days the cycle's muscle load is computed over, in the ONE order everything else walks them.
 *
 * The sequence counterpart of `countedDays`, and it exists for the same reason: the load is an array
 * positional against the days, and the view model walks the days back with a cursor to put each
 * day's bars under that day. Deriving the order twice is how a day's bars end up under its
 * neighbour. There is no overflow here — a self-paced day cannot fail to be placed, because the
 * canvas has a column for every day the phase holds.
 */
export function countedTapeDays(projection: TapeProjection): readonly (readonly ExerciseRow[])[] {
  return projection.days.map((day) => day.card.countedExercises);
}
